import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiMutationKeys, AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { Conversation } from "#types/types.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useCreateConversationMutation = () => {
    const client = useAiApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: AiMutationKeys.CreateConversation(),
        retry: 0,
        mutationFn: async() => {
            const data = await client.createConversation();

            return data;
        },
        onSuccess: ({ conversation }) => {
            queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetQuotaUsage() });

            // The conversation is only persisted server-side on its first message, so until then this
            // cached entry is the sole record of it. On the home page it's primed here by the eager create
            // (file attach) with no ConversationView observing it, so its gcTime falls back to the default
            // (5 min) — not the Infinity on getConversationQueryOptions, which only applies once observed.
            // Pin gcTime here (before setQueryData, so the built query inherits it) so a later submit finds
            // the entry cached instead of refetching getConversation for a not-yet-persisted conversation
            // and hitting a 404.
            queryClient.setQueryDefaults(AiQueryKeys.GetConversation(conversation.conversationId), { gcTime: Infinity });
            queryClient.setQueryData<Conversation>(AiQueryKeys.GetConversation(conversation.conversationId), () => conversation);
        }
    });
};
